import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { CodaltComponent } from '../../codalt.component';
import { Customer } from '../../customer';
import { CustomerProjectDialogComponent } from '../../customer-project-manage/customer-project-dialog/customer-project-dialog.component';
import { CustomerProject } from '../../customer-project.interface';
import { CustomerProjectService } from '../../services/customer-project/customer-project.service';
import { CustomerService } from '../../services/customer/customer.service';
import { WorkareaService } from '../../services/workarea/workarea.service';
import { projectFitsWorkarea, Workarea } from '../../workarea.class';

// Autocomplete controls hold the typed text until an option is picked; only a picked option is valid.
const requireOption: ValidatorFn = control =>
    control.value && typeof control.value === 'object' ? null : { required: true };

@Component({
    selector: 'app-workarea-transfer-dialog',
    templateUrl: './workarea-transfer-dialog.component.html',
    standalone: false,
})
export class WorkareaTransferDialogComponent extends CodaltComponent implements OnInit {
    customers: Customer[] = [];
    customer?: Customer;
    projects?: CustomerProject[];
    transferring = false;
    transferredTo?: CustomerProject;
    @Output() transferred = new EventEmitter<void>();

    form = new FormGroup({
        customer: new FormControl<Customer | string | null>(null, requireOption),
        project: new FormControl<CustomerProject | string | null>(null, requireOption),
    });

    displayName = (item?: { name: string } | null): string => item?.name ?? '';
    // Empty for a mixed drilling/non-drilling selection
    projectTypes = this.Settings.projectTypes.filter(t =>
        this.workareas.every(w => projectFitsWorkarea({ type: t.key } as CustomerProject, w))
    );

    constructor(
        @Inject(MAT_DIALOG_DATA) public workareas: Workarea[],
        private workareaService: WorkareaService,
        private customerService: CustomerService,
        private customerProjectService: CustomerProjectService,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit(): void {
        this.customerService.getList().subscribe(customers => (this.customers = customers));
    }

    filterByName<T extends { name: string }>(items: T[], value: T | string | null): T[] {
        const search = typeof value === 'string' ? value.toLowerCase() : '';
        return items.filter(item => item.name?.toLowerCase().includes(search));
    }

    selectCustomer(customer: Customer): void {
        this.customer = customer;
        this.projects = undefined;
        this.form.controls.project.reset();
        this.customerProjectService
            .getProjects(false, customer.id)
            .pipe(take(1))
            .subscribe(projects => {
                if (customer !== this.customer) {
                    return;
                }
                this.projects = [
                    ...projects.filter(p => this.workareas.every(w => projectFitsWorkarea(p, w))),
                    ...(this.projects ?? []),
                ];
            });
    }

    openNewProject(): void {
        CustomerProjectDialogComponent.open(this.dialog, this.customer, this.projectTypes)
            .afterClosed()
            .subscribe(project => {
                if (project) {
                    this.projects = [...(this.projects ?? []), project];
                    this.form.controls.project.setValue(project);
                }
            });
    }

    transfer(): void {
        if (this.form.invalid || this.transferring) {
            return;
        }
        this.transferring = true;
        const project = this.form.value.project as CustomerProject;
        const ids = this.workareas.map(w => w.id);
        this.workareaService.transfer(ids, project.id).subscribe({
            next: () => {
                this.transferredTo = project;
                this.transferring = false;
                this.transferred.emit();
            },
            error: () => (this.transferring = false),
        });
    }
}
